<template>
  <DxPopup
    width="100vw"
    height="100vh"
    :show-close-button="false"
    :visible="visible"
    :resize-enabled="false"
    :drag-enabled="false"
    :wrapper-attr="popupAttributes"
    @hidden="onHidden"
  >
    <template #title>
      <div class="prod-split-header">
        <div class="prod-split-back" @click="onCancel">
          <img src="@/assets/images/icons/arrow-back.svg" />
          <span>{{ $t("common.back") }}</span>
        </div>
        <div class="prod-split-title">{{ $t("stock_mobile.form.split_quantity") }}</div>
        <div class="prod-split-header-spacer"></div>
      </div>
    </template>
    <template #content>
      <div class="prod-split-content">
        <div class="prod-split-summary">
          <span class="prod-split-summary-label">{{ $t("stock_mobile.form.total_to_split") }}</span>
          <span class="prod-split-summary-value">{{ formattedTotal }} {{ measureUnitSymbol }}</span>
        </div>
        <div class="prod-split-sum-indicator" :class="{ 'prod-split-sum-ok': isSumOk, 'prod-split-sum-error': !isSumOk }">
          <span>{{ $t("stock_mobile.form.distributed") }}: {{ formattedDistributed }} {{ measureUnitSymbol }}</span>
          <span v-if="!isSumOk" class="prod-split-diff"> ({{ $t("stock_mobile.form.difference") }}: {{ formattedDiff }})</span>
        </div>
        <div class="dialog_title_separator"></div>
        <DxScrollView class="prod-split-scrollview" :use-native="true">
          <div class="prod-split-rows">
            <div
              v-for="(row, idx) in editableRows"
              :key="row.warehouse_uuid"
              class="prod-split-row"
              :class="{ 'prod-split-row-error': row._hasError }"
            >
              <div class="prod-split-row-top">
                <div class="prod-split-warehouse-name">{{ row.warehouse_name }}<template v-if="row.department_name"> ({{ row.department_name }})</template></div>
              </div>
              <div class="prod-split-row-parcels">{{ row.parcel_names }}</div>
              <div class="prod-split-row-surface">{{ row.total_surface }} ha</div>
              <div class="prod-split-qty-row">
                <span class="prod-split-qty-label">{{ $t("common.quantity") }} ({{ measureUnitSymbol }})</span>
                <DxNumberBox
                  :value="row.quantity"
                  :min="0"
                  :show-spin-buttons="false"
                  :show-clear-button="false"
                  :format="numberFormat"
                  styling-mode="outlined"
                  @value-changed="onQtyChanged(idx, $event)"
                />
                <div v-if="row._hasError" class="prod-split-row-error-msg">{{ $t("stock_mobile.form.error_quantity_gt_zero") }}</div>
              </div>
            </div>
          </div>
          <div class="prod-split-bottom-spacer"></div>
        </DxScrollView>
        <div v-if="globalError" class="prod-split-global-error">{{ globalError }}</div>
        <div class="prod-split-actions">
          <div
            class="sticky_action_button"
            :class="{ 'prod-split-btn-disabled': !isSumOk }"
            @click="onConfirm"
          >
            {{ $t("stock_mobile.form.confirm_and_save") }}
          </div>
        </div>
      </div>
    </template>
  </DxPopup>
</template>

<script>
import { DxPopup } from "devextreme-vue/popup";
import { DxScrollView } from "devextreme-vue/scroll-view";
import { DxNumberBox } from "devextreme-vue/number-box";
import { mapGetters } from "vuex";
import { getQuantityFormatter } from "@/helpers/stockHelper";

export default {
  name: "ProductionSplitPopup",
  components: {
    DxPopup,
    DxScrollView,
    DxNumberBox,
  },
  data() {
    return {
      visible: false,
      editableRows: [],
      totalQuantity: 0,
      measureUnitSymbol: "",
      globalError: null,
      promiseResolve: null,
      popupAttributes: {
        class: "product_history_dialog_wrapper prod-split-popup-wrapper",
      },
    };
  },
  computed: {
    ...mapGetters({
      quantityDecimalPlaces: "settings/getQuantityDecimalPlaces",
    }),
    numberFormat() {
      return getQuantityFormatter();
    },
    distributedTotal() {
      return this.editableRows.reduce((sum, r) => sum + (parseFloat(r.quantity) || 0), 0);
    },
    diff() {
      return Math.abs(
        parseFloat(this.distributedTotal.toFixed(this.quantityDecimalPlaces)) -
          parseFloat(this.totalQuantity.toFixed(this.quantityDecimalPlaces))
      );
    },
    isSumOk() {
      return this.diff < Math.pow(10, -(this.quantityDecimalPlaces));
    },
    formattedTotal() {
      return parseFloat(this.totalQuantity).toFixed(this.quantityDecimalPlaces);
    },
    formattedDistributed() {
      return parseFloat(this.distributedTotal).toFixed(this.quantityDecimalPlaces);
    },
    formattedDiff() {
      return parseFloat(this.diff).toFixed(this.quantityDecimalPlaces);
    },
  },
  methods: {
    /**
     * Open the popup with pre-computed split rows.
     * @param {Object} params
     * @param {Array}  params.rows           - Array of { warehouse_uuid, warehouse_name, department_uuid, quantity, layers, parcel_names, total_surface }
     * @param {number} params.totalQuantity  - Total quantity to be distributed
     * @param {string} params.measureUnitSymbol
     * @returns {Promise<Array|null>}        - Resolves with confirmed rows or null on cancel
     */
    open({ rows = [], totalQuantity = 0, measureUnitSymbol = "" } = {}) {
      return new Promise((resolve) => {
        this.editableRows = rows.map((r) => ({ ...r, _hasError: false }));
        this.totalQuantity = parseFloat(totalQuantity) || 0;
        this.measureUnitSymbol = measureUnitSymbol;
        this.globalError = null;
        this.promiseResolve = resolve;
        this.visible = true;
      });
    },
    closeWith(result) {
      this.visible = false;
      if (this.promiseResolve) {
        this.promiseResolve(result);
      }
      this.promiseResolve = null;
    },
    onHidden() {
      if (this.promiseResolve) {
        this.promiseResolve(null);
        this.promiseResolve = null;
      }
      this.editableRows = [];
      this.globalError = null;
    },
    onCancel() {
      this.closeWith(null);
    },
    onQtyChanged(idx, e) {
      const row = this.editableRows[idx];
      this.$set(this.editableRows, idx, {
        ...row,
        quantity: parseFloat(e.value) || 0,
        _hasError: false,
      });
      this.globalError = null;
    },
    onConfirm() {
      this.globalError = null;

      let hasRowError = false;
      this.editableRows = this.editableRows.map((row) => {
        const qty = parseFloat(row.quantity) || 0;
        const rowError = qty <= 0;
        if (rowError) hasRowError = true;
        return { ...row, _hasError: rowError };
      });

      if (hasRowError) {
        this.globalError = this.$t("stock_mobile.form.error_each_warehouse_qty_gt_zero");
        return;
      }

      if (!this.isSumOk) {
        this.globalError = this.$t("stock_mobile.form.error_distributed_sum_must_match", {
          total: this.formattedTotal,
          unit: this.measureUnitSymbol,
        });
        return;
      }

      const result = this.editableRows.map(({ _hasError, ...row }) => row);
      this.closeWith(result);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/stockMobile/productHistory.scss";

.prod-split-popup-wrapper {
  .dx-popup-content {
    padding: 0 !important;
  }
}

.prod-split-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 0;

  .prod-split-back {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: "Mulish", sans-serif;
    font-weight: 700;
    font-size: 14px;
    color: #096c27;
    cursor: pointer;
    min-width: 80px;
  }

  .prod-split-title {
    font-family: "Mulish", sans-serif;
    font-weight: 800;
    font-size: 18px;
    color: #323232;
    text-align: center;
    flex: 1;
  }

  .prod-split-header-spacer {
    min-width: 80px;
  }
}

.prod-split-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f5f5;
  overflow: hidden;
}

.prod-split-summary {
  background: #fff;
  padding: 12px 24px 8px;
  display: flex;
  align-items: center;
  gap: 8px;

  .prod-split-summary-label {
    font-family: "Mulish", sans-serif;
    font-size: 13px;
    color: #979797;
  }

  .prod-split-summary-value {
    font-family: "Mulish", sans-serif;
    font-weight: 800;
    font-size: 16px;
    color: #323232;
  }
}

.prod-split-sum-indicator {
  padding: 6px 24px 10px;
  font-family: "Mulish", sans-serif;
  font-size: 13px;
  font-weight: 700;
  background: #fff;

  &.prod-split-sum-ok {
    color: #096c27;
  }

  &.prod-split-sum-error {
    color: #c62828;
  }

  .prod-split-diff {
    font-weight: 400;
  }
}

.prod-split-scrollview {
  flex: 1;
  height: calc(100vh - 280px);
}

.prod-split-rows {
  padding: 12px 16px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.prod-split-row {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);

  &.prod-split-row-error {
    border: 1.5px solid #c62828;
  }

  .prod-split-row-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  .prod-split-warehouse-name {
    font-family: "Mulish", sans-serif;
    font-weight: 800;
    font-size: 15px;
    color: #323232;
  }

  .prod-split-row-parcels {
    font-family: "Mulish", sans-serif;
    font-size: 13px;
    color: #565656;
    margin-bottom: 2px;
  }

  .prod-split-row-surface {
    font-family: "Mulish", sans-serif;
    font-size: 12px;
    color: #979797;
    margin-bottom: 12px;
  }

  .prod-split-qty-row {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .prod-split-qty-label {
      font-family: "Mulish", sans-serif;
      font-size: 12px;
      color: #979797;
      font-weight: 700;
    }

    .dx-numberbox {
      background: #f9f9f9;
      border-radius: 8px;
    }
  }

  .prod-split-row-error-msg {
    font-family: "Mulish", sans-serif;
    font-size: 12px;
    color: #c62828;
  }
}

.prod-split-bottom-spacer {
  height: 120px;
}

.prod-split-global-error {
  margin: 0 16px 8px;
  padding: 10px 14px;
  background: #ffebee;
  border-radius: 8px;
  font-family: "Mulish", sans-serif;
  font-size: 13px;
  color: #c62828;
  font-weight: 600;
}

.prod-split-actions {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: #fff;
  border-radius: 20px 20px 0 0;
  box-shadow: 0 0 40px 4px rgba(0, 0, 0, 0.12);
  padding: 12px 24px;

  .prod-split-btn-disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}
</style>
